// Screen 15 — Seed import (dry-run reconciliation).
//
// Upload a CSV of seed ledger rows and run a DRY RUN: the backend parses the
// file, reports rows created / updated / skipped / unparseable, and reconciles
// the imported totals against the four expected anchors TO THE CENT. If any
// anchor mismatches (or any row is unparseable), the commit is BLOCKED — the
// operator must resolve the data before a real import is allowed.
//
// This screen only ever runs the dry run (POST /admin/import/seed?dry_run=1). A
// committing import is intentionally out of scope here — the gate exists so a
// mismatched seed can never be silently written.
//
// MONEY: expected / imported amounts are exact decimal STRINGS compared
// verbatim (string equality), never via float — a float compare could mask a
// sub-cent drift the ledger must catch.

import { useRef, useState } from "react";
import { toast } from "sonner";
import { CheckIcon, UploadIcon, XIcon } from "lucide-react";

import { api } from "@/api/client";
import { errMessage } from "@/lib/errMessage";
import { AdminShell } from "@/components/nexus/AdminShell";
import {
  DataTable,
  MoneyCell,
  StatCard,
  ToneBadge,
  type Column,
} from "@/components/nexus";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface ReconcileRow {
  label: string;
  expected: string;
  imported: string;
  match: boolean;
}

interface ImportStats {
  created: number;
  updated: number;
  skipped: number;
  unparseable: number;
}

interface DryRunResult {
  dry_run: boolean;
  stats: ImportStats;
  reconciliation: ReconcileRow[];
  commit_allowed: boolean;
}

export function ImportPage() {
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [csv, setCsv] = useState<string | null>(null);
  const [result, setResult] = useState<DryRunResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    setError(null);
    setCsv(await file.text());
  }

  async function runDryRun() {
    if (csv == null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<DryRunResult>(
        "/admin/import/seed?dry_run=1",
        { filename: fileName, csv },
      );
      setResult(res);
      if (res.commit_allowed) {
        toast.success("Dry run reconciled — all anchors match to the cent");
      } else {
        toast.error("Dry run found mismatches — commit blocked");
      }
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const reconcileColumns: Column<ReconcileRow>[] = [
    {
      key: "label",
      header: "Total",
      cell: (r) => <span className="text-nx-text">{r.label}</span>,
    },
    {
      key: "expected",
      header: "Expected",
      align: "right",
      cell: (r) => <MoneyCell value={r.expected} tone="muted" />,
    },
    {
      key: "imported",
      header: "Imported",
      align: "right",
      cell: (r) => (
        <MoneyCell value={r.imported} tone={r.match ? "default" : "debit"} />
      ),
    },
    {
      key: "match",
      header: "Match",
      align: "center",
      cell: (r) =>
        r.match ? (
          <CheckIcon className="inline size-4 text-nx-credit" aria-label="match" />
        ) : (
          <XIcon className="inline size-4 text-nx-debit" aria-label="mismatch" />
        ),
    },
  ];

  return (
    <AdminShell>
      <div className="mb-5">
        <h2 className="font-heading text-lg font-semibold">Seed import</h2>
        <p className="mt-1 max-w-2xl text-sm text-nx-text-2">
          Upload a CSV and run a dry-run reconciliation. Totals must match the
          four anchors to the cent — any mismatch (or unparseable row) blocks the
          commit.
        </p>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Dry run failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card className="mb-5 gap-3 p-5">
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            onChange={onFile}
            aria-label="Seed CSV file"
            className="block text-sm text-nx-text-2 file:mr-3 file:rounded-lg file:border file:border-nx-border file:bg-nx-bg-2 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-nx-text hover:file:border-nx-brand/40"
          />
          <Button
            type="button"
            disabled={csv == null || busy}
            onClick={runDryRun}
          >
            <UploadIcon /> Run dry-run
          </Button>
          {fileName && (
            <span className="font-mono text-xs text-nx-text-3 tabular-nums">
              {fileName}
            </span>
          )}
        </div>
        <p className="text-xs text-nx-text-3">
          Dry-run only — no rows are written. The commit gate stays blocked until
          every anchor reconciles.
        </p>
      </Card>

      {result && (
        <div className="grid gap-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard
              label="Created"
              value={result.stats.created}
              tone="credit"
            />
            <StatCard label="Updated" value={result.stats.updated} />
            <StatCard
              label="Skipped"
              value={result.stats.skipped}
              tone="muted"
            />
            <StatCard
              label="Unparseable"
              value={result.stats.unparseable}
              tone={result.stats.unparseable > 0 ? "debit" : "default"}
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-xs font-medium tracking-wide text-nx-text-2 uppercase">
                Reconciliation
              </h3>
              {result.commit_allowed ? (
                <ToneBadge tone="credit">Reconciled to the cent</ToneBadge>
              ) : (
                <ToneBadge tone="danger">Mismatch</ToneBadge>
              )}
            </div>
            <DataTable
              columns={reconcileColumns}
              rows={result.reconciliation}
              rowKey={(r) => r.label}
              empty="No totals to reconcile."
            />
          </div>

          {!result.commit_allowed && (
            <Alert variant="destructive">
              <AlertTitle>Commit blocked — resolve mismatches</AlertTitle>
              <AlertDescription>
                One or more anchors did not reconcile (or a row was
                unparseable). Fix the source data and re-run the dry run before
                committing.
              </AlertDescription>
            </Alert>
          )}

          <div>
            <Button
              type="button"
              disabled={!result.commit_allowed}
              title={
                result.commit_allowed
                  ? "Commit the reconciled import"
                  : "Commit blocked until all anchors reconcile"
              }
            >
              {result.commit_allowed ? "Commit import" : "Commit blocked"}
            </Button>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
